import React, { useEffect, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import AtsTooltip from 'ats/components/ats-tooltip/AtsTooltip';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import { jobPositionStore } from 'ats/pages/job-positions/JobPositionStore';
import { Loader } from 'components/layout/misc/Loader';
import { modalStore } from 'components/layout/modals/ModalStore';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { authStore } from 'shared/security/AuthStore';
import EmptyState from '../../../components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from '../../../components/empty-state/emptyStateIcons';
import {
	getJobCreatedDate,
	getJobDetailSections,
	getJobDisplayTitle,
	getJobIndustry,
	getJobLocations,
	getJobSchedule
} from '../openJobsData';
import { getOpenJobCardTheme } from '../openJobCardThemes';
import { hasAppliedToJob } from '../externalApplicationStorage';
import {
	OPEN_REFER_FORM_QUERY_PARAM,
	REFERRAL_TOKEN_QUERY_PARAM,
	buildReferFormPath,
	buildReferSignInPath,
	clearReferralToken,
	getStoredReferralToken,
	storeReferralToken
} from '../referralStorage';
import ExternalApplicationCreate from './ExternalApplicationCreate';
import ReferralCreate from './ReferralCreate';
import ReferralSignInPending from './ReferralSignInPending';

const MODAL_OPTIONS = { isV2: true, position: 'center' };

function getFirstName(name) {
	return (
		String(name || '')
			.trim()
			.split(/\s+/)[0] || ''
	);
}

function getInitials(name) {
	const parts = String(name || '')
		.trim()
		.split(/\s+/)
		.filter(Boolean);
	if (!parts.length) return '';
	const first = parts[0].charAt(0);
	const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
	return `${first}${last}`.toUpperCase();
}

function removeQueryParam(history, location, param) {
	const params = new URLSearchParams(location.search);
	params.delete(param);
	const search = params.toString();
	history.replace({
		pathname: location.pathname,
		search: search ? `?${search}` : ''
	});
}

function openReferralForm(job) {
	modalStore.openModal(MODAL_OPTIONS, <ReferralCreate job={job} />);
}

export default function OpenJobProfile({
	job,
	loading,
	theme = getOpenJobCardTheme(0)
}) {
	const history = useHistory();
	const location = useLocation();
	const jobId = job?.id;
	const [hasApplied, setHasApplied] = useState(() => hasAppliedToJob(jobId));
	const [referral, setReferral] = useState(null);

	useEffect(() => {
		setHasApplied(hasAppliedToJob(jobId));
	}, [jobId]);

	// The email link carries the token once; it is kept per Job in this
	// browser so Confirm application survives leaving and coming back.
	useEffect(() => {
		if (!jobId) {
			setReferral(null);
			return;
		}

		const urlToken = new URLSearchParams(location.search).get(
			REFERRAL_TOKEN_QUERY_PARAM
		);
		if (urlToken) {
			storeReferralToken(jobId, urlToken);
			removeQueryParam(history, location, REFERRAL_TOKEN_QUERY_PARAM);
			return;
		}

		const token = getStoredReferralToken(jobId);
		if (!token) {
			setReferral(null);
			return;
		}

		let cancelled = false;
		(async () => {
			const result = await jobPositionStore.getPublicReferral(token, jobId);
			if (cancelled) return;

			if (
				result?.status === 'success' &&
				Number(result.data?.jobId) === Number(jobId)
			) {
				setReferral({ ...result.data, token });
				return;
			}

			if (result?.code === 'invalid_referral') {
				clearReferralToken(jobId);
			}
			setReferral(null);
		})();

		return () => {
			cancelled = true;
		};
	}, [jobId, location, history]);

	useEffect(() => {
		if (!job) return;
		if (
			!new URLSearchParams(location.search).has(OPEN_REFER_FORM_QUERY_PARAM)
		) {
			return;
		}

		removeQueryParam(history, location, OPEN_REFER_FORM_QUERY_PARAM);
		if (authStore.profile) {
			openReferralForm(job);
		}
	}, [job, location, history]);

	if (loading && !job) {
		return (
			<section className="open-job-profile">
				<Loader type="page-loader" />
			</section>
		);
	}

	if (!job) {
		return (
			<section className="open-job-profile">
				<EmptyState
					icon={EMPTY_STATE_ICONS.job}
					iconAlt="Job not found"
					message="Job not found"
					detail="This role may no longer be open."
				/>
			</section>
		);
	}

	const isLoggedIn = Boolean(authStore.profile);
	const isConfirmingReferral = Boolean(referral) && !hasApplied;

	const shareJob = async () => {
		await copyTextToClipboard(`${window.location.origin}/jobs/${job.id}`);
		notificationStore.pushSuccessNotification(
			'Job link copied',
			'The link to this role was copied to your clipboard.'
		);
	};

	const openApplyModal = () => {
		if (hasApplied) {
			return;
		}

		modalStore.openModal(
			MODAL_OPTIONS,
			<ExternalApplicationCreate
				job={job}
				referral={isConfirmingReferral ? referral : null}
				handleSuccess={() => {
					setHasApplied(true);
					setReferral(null);
				}}
				onReferralInvalid={() => setReferral(null)}
			/>
		);
	};

	const continueReferralAfterSignIn = async token => {
		modalStore.closeDialog();
		history.replace(buildReferFormPath(job.id));
		authStore.setToken(token, null);
		await authStore.syncLoggedUserProfile();
	};

	const openSignInTab = () =>
		window.open(buildReferSignInPath(job.id), '_blank');

	const startReferral = () => {
		if (isLoggedIn) {
			openReferralForm(job);
			return;
		}

		if (!openSignInTab()) {
			history.push(buildReferSignInPath(job.id));
			return;
		}

		modalStore.openModal(
			MODAL_OPTIONS,
			<ReferralSignInPending
				onReopen={openSignInTab}
				onSignedIn={continueReferralAfterSignIn}
			/>
		);
	};

	const metaRows = [
		{ icon: '/ats/icons/clients-gray-icon.svg', text: getJobIndustry(job) },
		{ icon: '/ats/icons/calendar-gray-icon.svg', text: getJobSchedule(job) },
		{ icon: '/ats/icons/location-gray-icon.svg', text: getJobLocations(job) }
	].filter(row => row.text);

	const sections = getJobDetailSections(job);
	const createdDate = getJobCreatedDate(job);
	const owner = job.owner?.name ? job.owner : null;
	const referrerFirstName = getFirstName(referral?.referrerName);

	const referButton = (
		<button
			type="button"
			className="open-job-profile-refer"
			onClick={startReferral}
			aria-label={isLoggedIn ? 'Refer' : 'Refer (requires signing in to MIRA)'}>
			Refer
		</button>
	);

	return (
		<section className="open-job-profile">
			<div
				className={`open-job-profile-hero is-${theme.tone}`}
				style={{ backgroundColor: theme.background }}>
				<div className="open-job-profile-toolbar">
					<Link className="open-job-profile-back" to="/jobs">
						<img src="/ats/icons/chevron-left-white-icon.svg" alt="" />
						Back
					</Link>
					{createdDate ? (
						<span className="open-job-profile-created">
							Created: {createdDate}
						</span>
					) : null}
				</div>
				<div className="open-job-profile-heading">
					<div className="open-job-profile-title">
						{isConfirmingReferral ? (
							<p className="open-job-profile-referral-note">
								{referrerFirstName
									? `${referrerFirstName} referred you for this position.`
									: 'You were referred for this position.'}
							</p>
						) : null}
						<h1>{getJobDisplayTitle(job)}</h1>
					</div>
					<div className="open-job-profile-actions">
						<button
							type="button"
							className="open-job-profile-share"
							onClick={shareJob}
							aria-label="Share">
							<img src="/ats/icons/share-white-icon.svg" alt="" />
						</button>
						{isLoggedIn ? (
							referButton
						) : (
							<AtsTooltip text="Sign in to MIRA to refer" placement="top">
								{referButton}
							</AtsTooltip>
						)}
						<button
							type="button"
							className={`open-job-profile-apply${
								hasApplied ? ' is-applied' : ''
							}`}
							onClick={openApplyModal}
							disabled={hasApplied}>
							{hasApplied ? (
								<img
									src="/ats/icons/external-application-applied-check.svg"
									alt=""
								/>
							) : null}
							{hasApplied
								? 'Applied'
								: isConfirmingReferral
									? 'Confirm Application'
									: 'Apply'}
						</button>
					</div>
				</div>
			</div>
			{metaRows.length ? (
				<div className="open-job-profile-meta">
					{metaRows.map(row => (
						<div className="open-job-profile-meta-item" key={row.icon}>
							<span className="open-job-profile-meta-icon">
								<img src={row.icon} alt="" />
							</span>
							<span>{row.text}</span>
						</div>
					))}
				</div>
			) : null}
			<div className="open-job-profile-body">
				{sections.map(section => (
					<div className="open-job-profile-section" key={section.key}>
						<h2>{section.title}</h2>
						{section.html ? (
							<div
								className="open-job-profile-html"
								dangerouslySetInnerHTML={{ __html: section.html }}
							/>
						) : (
							<ul>
								{section.items.map((item, index) => (
									<li key={index}>{item}</li>
								))}
							</ul>
						)}
					</div>
				))}
				{owner ? (
					<div className="open-job-profile-owner">
						<div className="open-job-profile-owner-card">
							<UserProfileAvatar
								name={owner.name}
								imageUrl={owner.imageUrl}
								placeholderText={getInitials(owner.name)}
								showTooltip={false}
								size={64}
							/>
							<div className="open-job-profile-owner-identity">
								<span className="open-job-profile-owner-name">
									{owner.name}
								</span>
								{owner.role ? (
									<span className="open-job-profile-owner-role">
										{owner.role}
									</span>
								) : null}
							</div>
						</div>
						<div className="open-job-profile-owner-copy">
							<p>
								<strong>What happens after you apply</strong>
								<br />
								I’ll review your application and get in touch if your experience
								fits what we’re looking for.
							</p>
						</div>
					</div>
				) : null}
			</div>
		</section>
	);
}
